import { round } from "lodash-es";
import { Invoice, InvoiceType } from "src/app/backend/models/invoice/invoice";

const ROUNDING_GAP_TOLERANCE = 0.05;
const STANDARD_VAT_RATE = 0.22;

export interface InvoiceSummaryDisplayAdjustments {
  displayEnpavAmount: number;
  displayVatTaxAmounts: Array<{ tax: number; taxAmount: number }>;
}

/**
 * Explicit, `Invoice`-free context describing the fiscal partials that are
 * actually visible in the invoice summary (screen + print). Display adjustments
 * absorb small gaps between visible row sums and backend totals.
 */
export interface InvoiceSummaryDisplayAdjustmentsInput {
  /** Backend `taxedTotal` (sum-then-round). Target total at 2 dp. */
  taxedTotal: number;

  /** Target imponibile IVA (`vatRateableTotal` or `rateableTotal`). */
  imponibileIva: number;

  /** Stamp tax row shown. */
  stampTaxVisible: boolean;
  stampTaxAmount: number;

  medicalServicesRateable: number;
  drugsAssignmentRateable: number;
  parapharmaceuticalRateable: number;
  productsSaleRateable: number;

  /** ENPAV row shown. */
  enpavVisible: boolean;
  enpavAmount: number;

  /** Advance row shown; deducted only when `advanceDeductible`. */
  advanceVisible: boolean;
  advanceDeductible: boolean;
  advanceTotalAmount: number;

  /** Fattura d'acconto: l'importo va incluso nell'imponibile (non nelle categorie). */
  isAdvanceInvoice: boolean;

  /** Per-rate VAT amounts; each non-zero amount is a visible row. */
  vatSummaryLines: Array<{ tax: number; taxAmount: number }>;

  /** Non-taxable ("Non imponibile") row shown. */
  nonImponibileVisible: boolean;
  noVatRateableTotal: number;
  notTaxableTotal: number;
}

/**
 * Case 1 left-hand sum: Prestazioni + Farmaci + Parafarmaci + Prodotti +
 * Acconto + ENPAV (backend), with advance handled by document type.
 */
function computeImponibileFromCategoryPartials(
  input: InvoiceSummaryDisplayAdjustmentsInput
): number {
  let imponibile = 0;

  imponibile += round(input.medicalServicesRateable, 2);
  imponibile += round(input.drugsAssignmentRateable, 2);
  imponibile += round(input.parapharmaceuticalRateable, 2);
  imponibile += round(input.productsSaleRateable, 2);

  if (input.enpavVisible) {
    imponibile += round(input.enpavAmount, 2);
  }

  if (input.advanceVisible) {
    const advanceAmount = round(input.advanceTotalAmount, 2);

    if (input.isAdvanceInvoice) {
      imponibile += advanceAmount;
    } else if (input.advanceDeductible) {
      imponibile -= advanceAmount;
    }
  }

  return imponibile;
}

function getNonImponibileAmount(
  input: InvoiceSummaryDisplayAdjustmentsInput
): number {
  return input.noVatRateableTotal || input.notTaxableTotal || 0;
}

function computeCase1Target(
  input: InvoiceSummaryDisplayAdjustmentsInput
): number {
  return (
    round(input.imponibileIva, 2) + round(getNonImponibileAmount(input), 2)
  );
}

function computeCase1PartialSum(
  input: InvoiceSummaryDisplayAdjustmentsInput
): number {
  return round(computeImponibileFromCategoryPartials(input), 2);
}

function isStandardVatRate(tax: number): boolean {
  return round(tax, 4) === STANDARD_VAT_RATE || round(tax, 2) === 22;
}

function findVatAdjustmentTargetIndex(
  vatSummaryLines: Array<{ tax: number; taxAmount: number }>
): number | null {
  const standardVatIndex = vatSummaryLines.findIndex(
    (line) => line.taxAmount !== 0 && isStandardVatRate(line.tax)
  );

  if (standardVatIndex !== -1) {
    return standardVatIndex;
  }

  const firstNonZeroIndex = vatSummaryLines.findIndex(
    (line) => line.taxAmount !== 0
  );

  return firstNonZeroIndex !== -1 ? firstNonZeroIndex : null;
}

function isWithinRoundingTolerance(gap: number): boolean {
  return Math.abs(gap) < ROUNDING_GAP_TOLERANCE;
}

/**
 * Computes display-only adjustments when |gap| < 0,05:
 *
 * Case 1 — compare (categorie + acconto + ENPAV) vs (Imponibile IVA + Non
 * imponibile); absorb gap into ENPAV when visible.
 *
 * Case 2 — compare (Imponibile IVA + IVA + Non imponibile + bollo se visibile)
 * vs `taxedTotal`; absorb gap into IVA at 22% or first non-zero rate.
 */
export function computeInvoiceSummaryDisplayAdjustments(
  input: InvoiceSummaryDisplayAdjustmentsInput
): InvoiceSummaryDisplayAdjustments {
  const displayVatTaxAmounts = input.vatSummaryLines.map((line) => ({
    tax: line.tax,
    taxAmount: line.taxAmount
  }));

  let displayEnpavAmount = input.enpavAmount;

  const gap1 = round(
    computeCase1Target(input) - computeCase1PartialSum(input),
    2
  );

  if (isWithinRoundingTolerance(gap1) && input.enpavVisible) {
    displayEnpavAmount = round(input.enpavAmount + gap1, 2);
  }

  let partialSum2 = round(input.imponibileIva, 2);

  for (const line of input.vatSummaryLines) {
    if (line.taxAmount !== 0) {
      partialSum2 += round(line.taxAmount, 2);
    }
  }

  partialSum2 += round(getNonImponibileAmount(input), 2);

  if (input.stampTaxVisible) {
    partialSum2 += round(input.stampTaxAmount, 2);
  }

  const gap2 = round(round(input.taxedTotal, 2) - partialSum2, 2);

  if (isWithinRoundingTolerance(gap2)) {
    const targetIndex = findVatAdjustmentTargetIndex(input.vatSummaryLines);

    if (targetIndex !== null) {
      displayVatTaxAmounts[targetIndex] = {
        ...displayVatTaxAmounts[targetIndex],
        taxAmount: round(displayVatTaxAmounts[targetIndex].taxAmount + gap2, 2)
      };
    }
  }

  return {
    displayEnpavAmount,
    displayVatTaxAmounts
  };
}

/**
 * Maps an `Invoice` plus caller-provided visibility flags to the explicit
 * adjustments input. Visibility differs between the screen summary and the
 * print view model, so flags are passed in rather than recomputed here.
 */
export function buildInvoiceSummaryDisplayAdjustmentsInput({
  invoice,
  stampTaxVisible,
  enpavVisible,
  nonImponibileVisible,
  advanceDeductible
}: {
  invoice: Invoice;
  stampTaxVisible: boolean;
  enpavVisible: boolean;
  nonImponibileVisible: boolean;
  advanceDeductible: boolean;
}): InvoiceSummaryDisplayAdjustmentsInput {
  const advanceTotalAmount = invoice.advanceTotalAmount ?? 0;

  return {
    taxedTotal: invoice.taxedTotal,
    imponibileIva: invoice.vatRateableTotal || invoice.rateableTotal || 0,
    stampTaxVisible,
    stampTaxAmount: invoice.stampTaxAmount ?? 0,
    medicalServicesRateable: invoice.medicalServicesRateable ?? 0,
    drugsAssignmentRateable: invoice.drugsAssignmentRateable ?? 0,
    parapharmaceuticalRateable: invoice.parapharmaceuticalRateable ?? 0,
    productsSaleRateable: invoice.productsSaleRateable ?? 0,
    enpavVisible,
    enpavAmount: invoice.enpavAmount ?? 0,
    advanceVisible: advanceTotalAmount > 0,
    advanceDeductible,
    advanceTotalAmount,
    isAdvanceInvoice: invoice.type === InvoiceType.ADVANCE_INVOICE,
    vatSummaryLines: (invoice.vatSummaryLines ?? []).map((line) => ({
      tax: line.tax,
      taxAmount: line.detail.taxAmount
    })),
    nonImponibileVisible,
    noVatRateableTotal: invoice.noVatRateableTotal ?? 0,
    notTaxableTotal: invoice.notTaxableTotal ?? 0
  };
}
